import { useState, useEffect, useCallback } from "react";
import type { LensSDKException } from "lens-platform-sdk";
import { Modal } from "@k8slens/lds";

import { useAnalytics } from "src/hooks/useAnalytics";

import Notification from "src/components/Notification/Notification";
import { getUniqueEmailsArray, validateEmail } from "src/services/email";
import styles from "src/components/Business/InviteAdminMode.module.css";
import TrackedButton from "src/components/Button/TrackedButton";

interface Props {
  open: boolean;
  inviting: boolean;
  inviteError?: LensSDKException;
  onModalClose: () => void;
  loadingBusinessUsers: boolean;
  onClickInvite: (emails: string) => void;
}

const cancelButtonLabel = "Cancel";
const title = "Invite Administrator";

const emailRequiredMessage = "Required";
const emailInvalidMessage = "Invalid email address(es)";

const inviteButtonLabel = "Invite";

const InviteAdminModal = ({
  open,
  inviting,
  inviteError,
  onModalClose,
  loadingBusinessUsers,
  onClickInvite,
}: Props) => {
  const { trackButtonClicked } = useAnalytics();
  const [emails, setEmails] = useState<string>("");
  const [emailsValid, setEmailsValid] = useState<boolean>(false);
  const [emailsErrorMessage, setEmailsErrorMessage] = useState<string>("");

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setEmails("");
    setEmailsValid(false);
    setEmailsErrorMessage("");
  }, []);

  /**
   * Return the errorMessage if the emails are invalid or undefined if valid
   */
  const emailsValidate = useCallback((emails: string) => {
    // emails is a empty string
    if (emails.length === 0) {
      return emailRequiredMessage;
    }

    const emailsArray = getUniqueEmailsArray(emails);
    const isAllEmailValid = emailsArray.every(validateEmail);

    if (!isAllEmailValid) {
      return emailInvalidMessage;
    }

    return undefined;
  }, []);

  useEffect(() => {
    const result = emailsValidate(emails);

    setEmailsValid(result === undefined);
    setEmailsErrorMessage(result ?? "");
  }, [emails, emailsValidate]);

  return (
    <Modal
      title={title}
      size="md"
      contentProps={{
        className: styles.inviteAdminModalContent,
      }}
      onClose={() => {
        if (!inviting) {
          onModalClose();
          reset();
        }
      }}
      isOpen={open}
      buttonBarProps={{
        type: "grid",
        gridSize: 3,
      }}
      footer={
        <>
          <button
            aria-label={cancelButtonLabel}
            type="button"
            onClick={() => {
              if (!inviting) {
                trackButtonClicked(cancelButtonLabel);
                onModalClose();
                reset();
              }
            }}
            className="lds-button"
          >
            {cancelButtonLabel}
          </button>
          <div />
          <TrackedButton
            loading={inviting || loadingBusinessUsers}
            label={inviteButtonLabel}
            id="inviteAdminButton"
            buttonType="submit"
            primary
            onClick={() => {
              trackButtonClicked(inviteButtonLabel);
              onClickInvite(emails);
            }}
            disabled={emailsValid === false}
          />
        </>
      }
    >
      <form aria-label={title} autoComplete="off">
        {inviteError ? (
          <Notification
            level="error"
            message={inviteError?.message ?? "unknown"}
            type="flash"
            className={styles.errorNotification}
          />
        ) : null}
        <fieldset>
          <div className={styles.emailTextareaLabel}>
            <span>Invite Users</span>
          </div>
          <textarea
            value={emails}
            onChange={({ target: { value } }) => {
              setEmails(value);
            }}
            autoComplete="off"
            name="adminEmails"
            placeholder="Comma separated list of Email addresses"
            className={styles.emailTextarea}
            spellCheck="false"
          />
          {emailsErrorMessage.length > 0 ? (
            <div className={styles.emailsErrorMessage}>
              <span>{emailsErrorMessage}</span>
            </div>
          ) : null}
          {emailsValid ? (
            <div className={styles.emailsMessage}>
              <span>{`Invite ${getUniqueEmailsArray(emails).length} administrator(s)`}</span>
            </div>
          ) : null}
        </fieldset>
      </form>
    </Modal>
  );
};

export default InviteAdminModal;
